import { fetchQuery, graphql } from 'react-relay';
import RelayEnvironment from 'src/relay/environment';
import WebFont from 'webfontloader';
import { designFontLoaderQuery } from './__generated__/designFontLoaderQuery.graphql';

const FontsQuery = graphql`
    query designFontLoaderQuery {
        fontFamilies {
            name
            cssUrl
        }
    }
`;

type Font = {
    readonly name: string;
    readonly cssUrl: string;
};

const fontNameAsKey = (fontName: string) => {
    return fontName.replace(/\s+/g, '').replace(/['"]/g, '').toLowerCase();
};

const getFontVariation = (fontWeight: string) => {
    switch (fontWeight) {
        case '100':
            return 'n1';
        case '200':
            return 'n2';
        case '300':
            return 'n3';
        case '400':
            return 'n4';
        case '500':
            return 'n5';
        case '600':
            return 'n6';
        case '700':
            return 'n7';
        case '800':
            return 'n8';
        case '900':
            return 'n9';
        case 'italic 100':
            return 'i1';
        case 'italic 200':
            return 'i2';
        case 'italic 300':
            return 'i3';
        case 'italic 400':
            return 'i4';
        case 'italic 500':
            return 'i5';
        case 'italic 600':
            return 'i6';
        case 'italic 700':
            return 'i7';
        case 'italic 800':
            return 'i8';
        case 'italic 900':
            return 'i9';
    }
};

async function downloadFontsOfDesign(design: MPlaza.Design, downloadedFonts: readonly Font[]) {
    const variationMap: { [key: string]: string[] } = {};
    // Get all the fonts first
    const fonts: string[] = [];
    const designItems = design.get('designItems');
    if (!designItems) return;

    for (const item of designItems.models) {
        const json = item.get('json');
        if (!json) continue;

        const data = JSON.parse(json);
        const attributes = data[1];

        //Get the global font family or from the first letter (in case of curved text)
        let fontFamilyName =
            attributes.fontFamily ||
            (attributes.children && attributes.children.length > 0 && attributes.children[0].length > 1 ? attributes.children[0][1].fontFamily : null);

        if (fontFamilyName) fontFamilyName = fontNameAsKey(fontFamilyName);

        const fontWeight = attributes?.data?.textInfo?.fontWeight;
        const fontVariationToAdd = getFontVariation(fontWeight && fontWeight.toString());

        if (fontVariationToAdd && fontFamilyName) {
            if (!variationMap[fontFamilyName]) {
                variationMap[fontFamilyName] = [fontVariationToAdd];
            } else if (!variationMap[fontFamilyName].includes(fontVariationToAdd)) {
                // If the variation is not already in the map, add it
                variationMap[fontFamilyName].push(fontVariationToAdd);
            }
            if (!fonts.includes(fontFamilyName)) {
                fonts.push(fontFamilyName);
            }
        }
    }

    const fontFamilies: readonly Font[] = fonts
        .map((fontName) => downloadedFonts.find((fontFamily) => fontNameAsKey(fontFamily.name).toLowerCase() == fontName.toLowerCase()) || null)
        .filter((x): x is Font => x != null);

    if (fontFamilies.length > 0) {
        // Build font families with variations for webfontloader (e.g., "FontName:n4,n7,i4")
        const families = fontFamilies.map((font) => {
            const fontKey = fontNameAsKey(font.name);
            const variations = variationMap[fontKey];
            if (variations && variations.length > 0) {
                return `${font.name}:${variations.join(',')}`;
            }
            return font.name;
        });

        const urls = fontFamilies.map((font) => font.cssUrl).filter(Boolean);

        await new Promise<void>((resolve, reject) => {
            WebFont.load({
                custom: {
                    families,
                    urls
                },
                active: () => resolve(),
                inactive: () => reject(new Error('Failed to load fonts')),
                timeout: 10000
            });
        });
    }
}

/**
 * Fetches fonts from GraphQL and loads them for the given design.
 * Should be called before renderDesignAsync to ensure fonts are available.
 */
export async function loadFontsForDesign(design: MPlaza.Design): Promise<void> {
    try {
        console.log('Loading fonts for design:', design.get('docID'));
        const fontsData = await fetchQuery<designFontLoaderQuery>(RelayEnvironment, FontsQuery, {}).toPromise();

        if (fontsData?.fontFamilies) {
            await downloadFontsOfDesign(design, fontsData.fontFamilies);
        }
    } catch (err) {
        console.error('Error loading fonts for design:', err);
    }
}
